import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { RestaurantStatus, ClosedReason } from '../status'

const RESTAURANT_STATUS_SYNC_KEY = 'restaurant-status-sync'

export interface RestaurantStatusResponse {
  status: RestaurantStatus
  closedReason: ClosedReason | null
  activeClosureId: number | null
  activeClosureEnd: number | null
  activeClosureComment: string | null
  canOpenNow: boolean
  serverTime: number
}

export function restaurantStatusQueryKey(restaurantId: number) {
  return ['restaurant-status', restaurantId] as const
}

export function broadcastRestaurantStatusSync(restaurantId: number) {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(
    RESTAURANT_STATUS_SYNC_KEY,
    JSON.stringify({
      restaurantId,
      timestamp: Date.now(),
    })
  )
}

export function useRestaurantStatus(restaurantId: number) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: restaurantStatusQueryKey(restaurantId),
    queryFn: async () => {
      const response = await apiClient.get<RestaurantStatusResponse>(
        `/api/admin/restaurants/${restaurantId}/status`
      )
      return response.data
    },
    enabled: !!restaurantId,
    staleTime: 15_000,
    // Background polling so multi-user changes propagate even when the SSE
    // NotificationProvider is unavailable (e.g. legacy pages, dropped stream).
    // SSE invalidation still happens immediately on opening_times events; this
    // is the safety net.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== RESTAURANT_STATUS_SYNC_KEY || !event.newValue) {
        return
      }

      try {
        const parsed = JSON.parse(event.newValue) as { restaurantId?: number }
        if (parsed.restaurantId !== restaurantId) {
          return
        }

        void queryClient.invalidateQueries({ queryKey: restaurantStatusQueryKey(restaurantId) })
      } catch {
        // Ignore malformed values from other tabs/extensions.
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [queryClient, restaurantId])

  return query
}
